import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPlanPriceUsd,
  type BillingCadence,
} from '../config/commercialPlans';
import { isEnterpriseSeatBand, type SeatBandCode } from '../config/seatBands';
import { formatUsd } from '../lib/formatUsd';
import {
  getRepresentativeSeatCount,
  getTeamTotalPeriodKey,
  getTeamTotalUsd,
  shouldShowMarketBenchmark,
} from '../lib/pricingInsights';
import { cn } from '../lib/utils';

export function PricingMarketingHints({
  seatBand,
  billingCadence,
  className,
  tone = 'default',
}: {
  seatBand: SeatBandCode;
  billingCadence: BillingCadence;
  className?: string;
  tone?: 'default' | 'home';
}): React.JSX.Element {
  const { t } = useTranslation('pricing');
  const isEnterpriseBand = isEnterpriseSeatBand(seatBand);

  const bandHint = isEnterpriseBand
    ? t('marketing.enterpriseHint')
    : seatBand === '1-3'
      ? t('marketing.upgradeAtFour')
      : t('marketing.bestRateFromFour');

  const representativeSeatCount = getRepresentativeSeatCount(seatBand);
  const plusPerLicenseUsd = getPlanPriceUsd('plus', seatBand, billingCadence);
  const teamTotalUsd =
    representativeSeatCount !== null && plusPerLicenseUsd !== null
      ? getTeamTotalUsd(plusPerLicenseUsd, representativeSeatCount)
      : null;

  const showMarketBenchmark = shouldShowMarketBenchmark(seatBand);

  const textToneClass =
    tone === 'home' ? 'text-[var(--ir-muted)]' : 'text-gray-500';
  const accentToneClass =
    tone === 'home' ? 'text-brand-primary/75' : 'text-brand-primary/80';

  return (
    <div
      className={cn(
        'rounded-2xl border border-brand-primary/10 bg-brand-bg/70 px-5 py-4',
        className
      )}
    >
      <ul className={cn('space-y-2 text-sm leading-relaxed', textToneClass)}>
        <li>{bandHint}</li>
        {teamTotalUsd !== null && representativeSeatCount !== null ? (
          <li className={cn('font-medium', accentToneClass)}>
            {t('marketing.teamTotalExample', {
              count: representativeSeatCount,
              perLicense: formatUsd(plusPerLicenseUsd!),
              total: formatUsd(teamTotalUsd),
              period: t(getTeamTotalPeriodKey(billingCadence)),
            })}
          </li>
        ) : null}
        {showMarketBenchmark ? (
          <li>
            {t('marketing.marketBenchmark')}
          </li>
        ) : null}
      </ul>
      <p
        className={cn(
          'mt-3 border-t border-brand-primary/10 pt-3 text-xs leading-relaxed',
          textToneClass
        )}
      >
        {t('marketing.addonModules')}
      </p>
    </div>
  );
}
